// Vibe = overall wellbeing; green/yellow/red dot per check-in

export function computeVibe(checkin) {
  if (!checkin || (checkin.sleep == null && checkin.mood == null)) {
    return {
      level: "unknown",
      color: "#D9D9D9",
      tint: "#FFFFFF",
      emoji: "🫥",
      label: "Not yet",
    };
  }
  // Mood drives the card color — it's what people feel right now.
  // Sleep has its own emoji on the card, so we don't double-count it here.
  // A perfect 10 always wins — no tag can override that.
  // Otherwise rough tags force "rough" regardless of mood score.
  const mood = checkin.mood ?? 5;
  const roughTags = ["sick", "stressed", "anxious", "grouchy"];
  const hasRough = (checkin.tags || []).some((t) => roughTags.includes(t));
  if (mood >= 10) {
    return {
      level: "good",
      color: "#6F9577",
      tint: "#CDE6D2",
      emoji: "🟩",
      label: "Good",
    };
  }
  if (hasRough || mood <= 3) {
    return {
      level: "rough",
      color: "#B05C4C",
      tint: "#F1C5BD", // soft red
      emoji: "🟥",
      label: "Rough",
    };
  }
  if (mood <= 4) {
    return {
      level: "meh",
      color: "#C07B3C",
      tint: "#F5D3B1", // soft orange
      emoji: "🟧",
      label: "Low",
    };
  }
  if (mood <= 6) {
    return {
      level: "okay",
      color: "#C0A060",
      tint: "#F1E2B4", // soft yellow
      emoji: "🟨",
      label: "Okay",
    };
  }
  return {
    level: "good",
    color: "#6F9577",
    tint: "#CDE6D2", // soft green
    emoji: "🟩",
    label: "Good",
  };
}

export function needsLove(checkin) {
  if (!checkin) return false;
  const v = computeVibe(checkin);
  return v.level === "rough";
}

// Pet walk vibe: tint a pet's card based on how long ago it was last walked.
//   < 5h   → green (good)
//   5-6h   → yellow (okay)
//   7h     → orange (meh)
//   ≥ 8h   → red (overdue)
//   never  → neutral surface (no colour yet)
export function petWalkVibe(checkin) {
  const walks = (checkin?.walks || []).slice().sort((a, b) =>
    (a.at > b.at ? -1 : 1)
  );
  const last = walks[0];
  if (!last?.at) {
    return { level: "unknown", tint: "var(--surface)", hours: null };
  }
  const hours = (Date.now() - new Date(last.at).getTime()) / 3_600_000;
  if (hours < 5)  return { level: "good",  tint: "#CDE6D2", hours };
  if (hours < 7)  return { level: "okay",  tint: "#F1E2B4", hours };
  if (hours < 8)  return { level: "meh",   tint: "#F5D3B1", hours };
  return           { level: "rough", tint: "#F1C5BD", hours };
}

// Per-species care thresholds (hours between expected feeds / water changes).
// `water=null` means water change is available but not urgency-tinted (fish,
// reptile — their water change is more of a weekly housekeeping stamp).
export const PET_CARE_SPECIES = [
  { key: "dog",     label: "Dog",     emoji: "🐶", fedHrs: null, waterHrs: 18 },
  { key: "cat",     label: "Cat",     emoji: "🐱", fedHrs: 12,   waterHrs: 24 },
  { key: "rabbit",  label: "Rabbit",  emoji: "🐰", fedHrs: 12,   waterHrs: 24 },
  { key: "hamster", label: "Hamster", emoji: "🐹", fedHrs: 24,   waterHrs: 24 },
  { key: "turtle",  label: "Turtle",  emoji: "🐢", fedHrs: 48,   waterHrs: 168 },
  { key: "bird",    label: "Bird",    emoji: "🐦", fedHrs: 12,   waterHrs: 24 },
  { key: "fish",    label: "Fish",    emoji: "🐠", fedHrs: 24,   waterHrs: null },
  { key: "reptile", label: "Reptile", emoji: "🦎", fedHrs: 48,   waterHrs: null },
  { key: "other",   label: "Other",   emoji: "✨", fedHrs: 24,   waterHrs: 24 },
];

export function petSpeciesMeta(species) {
  return PET_CARE_SPECIES.find((s) => s.key === species) || PET_CARE_SPECIES[0];
}

// Care vibe for non-dog pets. Picks whichever of fed/water is most overdue
// relative to its species threshold and tints the card accordingly. Matches
// the petWalkVibe thresholds visually (good → rough) but scales per species.
export function petCareVibe(checkin, species) {
  const meta = petSpeciesMeta(species || "other");
  const fedAt = checkin?.fed_at;
  const waterAt = checkin?.water_change_at;
  const fedHrs = fedAt ? (Date.now() - new Date(fedAt).getTime()) / 3_600_000 : null;
  const waterHrs = waterAt ? (Date.now() - new Date(waterAt).getTime()) / 3_600_000 : null;
  // Normalised ratio (0 = just done, 1 = exactly at threshold, >1 = overdue)
  const fedRatio = meta.fedHrs ? (fedHrs == null ? 1.2 : fedHrs / meta.fedHrs) : 0;
  const waterRatio = meta.waterHrs ? (waterHrs == null ? 1.2 : waterHrs / meta.waterHrs) : 0;
  const worst = Math.max(fedRatio, waterRatio);
  if (fedHrs == null && waterHrs == null && !meta.fedHrs && !meta.waterHrs) {
    return { level: "unknown", tint: "var(--surface)", fedHrs, waterHrs };
  }
  let level, tint;
  if (worst < 0.6)      { level = "good";  tint = "#CDE6D2"; }
  else if (worst < 0.9) { level = "okay";  tint = "#F1E2B4"; }
  else if (worst < 1.2) { level = "meh";   tint = "#F5D3B1"; }
  else                  { level = "rough"; tint = "#F1C5BD"; }
  return { level, tint, fedHrs, waterHrs, meta };
}

export function weeklyVibeSummary(history) {
  if (!history || history.length === 0) {
    return { phrase: "No check-ins yet", tone: "neutral" };
  }
  const vibes = history.map((c) => computeVibe(c).level);
  const good = vibes.filter((v) => v === "good").length;
  const rough = vibes.filter((v) => v === "rough").length;
  const okay = vibes.filter((v) => v === "okay").length;
  if (good >= rough + okay)
    return { phrase: "Mostly good ✨", tone: "good" };
  if (rough >= good + okay)
    return { phrase: "A tough week 🫂", tone: "rough" };
  if (good > rough) return { phrase: "Mixed but trending up 🌤", tone: "okay" };
  if (rough > good) return { phrase: "Mixed with some rough days 💗", tone: "rough" };
  return { phrase: "A mixed week 🌤", tone: "okay" };
}

// Translate today's family check-ins into a human sentence
export function atAGlance(members, checkins) {
  const hour = new Date().getHours();
  const partOfDay =
    hour < 4 ? "night"
    : hour < 11 ? "morning"
    : hour < 15 ? "day"
    : hour < 18 ? "afternoon"
    : "evening";
  const humans = (members || []).filter((m) => m.role !== "pet");
  const pets = (members || []).filter((m) => m.role === "pet");
  const memberCount = humans.length;
  if (memberCount === 0) return "Add your first family member to get started";

  // Mood/sleep logs are humans-only — pets don't have a mood slider. Any
  // pet activity (walk, scheduled next walk, feed, water stamp) only
  // affects the "has anyone touched the dashboard today?" signal so the
  // headline doesn't lie when the dog has been walked but no human has
  // checked in yet.
  const humanIds = new Set(humans.map((m) => m.id));
  const petIds = new Set(pets.map((m) => m.id));
  const logged = checkins.filter(
    (c) => humanIds.has(c.member_id) && (c.sleep != null || c.mood != null)
  );
  // petTouched counts only REAL care actions that happened today — not
  // future plans. Scheduling a walk for later doesn't mean the dog has
  // been walked yet, so we never let `next_walk_at` count. Same for the
  // single-stamp `fed_at` / `water_change_at` fields that the pet card
  // buttons write to.
  const petTouched = checkins.some(
    (c) =>
      petIds.has(c.member_id) &&
      ((c.walks && c.walks.length > 0) ||
        !!c.fed_at ||
        !!c.water_change_at ||
        (c.feeds && c.feeds.length > 0) ||
        (c.water_changes && c.water_changes.length > 0))
  );

  if (logged.length === 0) {
    if (petTouched) return `Quiet ${partOfDay} — pets sorted, humans not yet`;
    return `Quiet ${partOfDay} — nobody's checked in yet`;
  }

  const vibes = logged.map((c) => computeVibe(c).level);
  const good = vibes.filter((v) => v === "good").length;
  const rough = vibes.filter((v) => v === "rough").length;
  const okay = vibes.filter((v) => v === "okay").length;

  if (rough >= 2) return `Rough ${partOfDay} for some 💗`;
  if (rough === 1 && good === 0) return `A bumpy ${partOfDay} — someone might need a hug`;
  if (rough === 1) return `Mixed ${partOfDay} — one rough spot`;
  if (good >= 2 && okay + rough === 0) return `A calm ${partOfDay} ✨`;
  if (good >= memberCount - 1) return `A calm ${partOfDay} ✨`;
  if (good > okay) return `Good ${partOfDay}, mostly bright`;
  return `Mixed start to the ${partOfDay}`;
}

export function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const secs = Math.max(0, Math.round((now - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export const ADULT_PLAN_PRESETS = [
  "At the office",
  "Home by 5",
  "Home by 7",
  "Gym after work",
  "Dinner at 19",
  "Picking up kids",
  "Out with friends",
];

export const KID_PLAN_PRESETS = [
  "At school",
  "Football practice",
  "Piano lesson",
  "Playing outside",
  "Quiet night in",
  "Sleepover",
  "Out with friends",
];

export function planPresetsFor(role, customAdult = [], customKid = []) {
  const base = role === "kid" ? KID_PLAN_PRESETS : ADULT_PLAN_PRESETS;
  const custom = role === "kid" ? customKid : customAdult;
  const merged = [...base];
  for (const p of custom || []) {
    if (p && !merged.includes(p)) merged.push(p);
  }
  return merged;
}

// Legacy — union of both, used only for usage-count lookup
export const PLAN_PRESETS = Array.from(
  new Set([...ADULT_PLAN_PRESETS, ...KID_PLAN_PRESETS])
);

export const CHECKIN_REACTIONS = ["❤️", "👍", "👀", "🤗", "🫶", "💪"];

// One emoji per score 1-10
const MOOD_SCALE = ["😠", "🙁", "🫤", "😶", "🙂", "😊", "😀", "😄", "😃", "🥰"];
const SLEEP_SCALE = ["😬", "😫", "🫤", "😐", "🤔", "💤", "😴", "😴", "🙂", "😃"];

export function moodEmoji(v) {
  if (v == null) return "";
  const idx = Math.max(1, Math.min(10, Math.round(v))) - 1;
  return MOOD_SCALE[idx];
}

export function sleepEmoji(v) {
  if (v == null) return "";
  const idx = Math.max(1, Math.min(10, Math.round(v))) - 1;
  return SLEEP_SCALE[idx];
}

// localStorage tracked plan usage — show presets that are actually used often
const PLAN_USAGE_KEY = "famfeel_plan_usage";

export function getPlanUsage() {
  try {
    return JSON.parse(localStorage.getItem(PLAN_USAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

export function bumpPlanUsage(text) {
  if (!text) return;
  const usage = getPlanUsage();
  usage[text] = (usage[text] || 0) + 1;
  try {
    localStorage.setItem(PLAN_USAGE_KEY, JSON.stringify(usage));
  } catch {}
}

// Per-device list of plan ideas the user has dismissed. Stored as a string[]
// so we can JSON-roundtrip cleanly and keep the API symmetrical with usage.
const PLAN_DISMISSED_KEY = "famfeel_plan_dismissed";

export function getDismissedPlans() {
  try {
    const raw = JSON.parse(localStorage.getItem(PLAN_DISMISSED_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

export function dismissPlan(text) {
  if (!text) return;
  const set = new Set(getDismissedPlans());
  set.add(text);
  try {
    localStorage.setItem(PLAN_DISMISSED_KEY, JSON.stringify([...set]));
  } catch {}
}

export function restorePlans() {
  try {
    localStorage.removeItem(PLAN_DISMISSED_KEY);
  } catch {}
}

export const TIMEZONES = [
  "UTC",
  "Europe/London",
  "Europe/Oslo",
  "Europe/Copenhagen",
  "Europe/Stockholm",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Dublin",
  "Europe/Lisbon",
  "Europe/Helsinki",
  "Europe/Athens",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Vancouver",
  "America/Sao_Paulo",
  "America/Mexico_City",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Singapore",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Shanghai",
  "Asia/Hong_Kong",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Pacific/Auckland",
];
